// src/components/common/Tabs.tsx
//
// Componente generico de Tabs (Tab Links + Tab Content) reutilizavel.
// Sem dependencia externa. Acessibilidade: role="tablist"/"tab"/"tabpanel",
// aria-selected, aria-controls; navegacao por teclado: ArrowLeft/ArrowRight,
// Home/End. Tematizavel via prop `theme`.
import React, { useEffect, useRef, useState } from 'react';
import './Tabs.css';

export interface TabItem {
  id: string;
  label: string;
  badge?: string;
  content: React.ReactNode;
}

interface TabsProps {
  items: TabItem[];
  defaultActiveId?: string;
  theme: 'dark' | 'light';
  ariaLabel?: string;
  className?: string;
}

const Tabs: React.FC<TabsProps> = ({
  items,
  defaultActiveId,
  theme,
  ariaLabel,
  className,
}) => {
  const initialId =
    defaultActiveId && items.some((i) => i.id === defaultActiveId)
      ? defaultActiveId
      : items[0]?.id ?? '';
  const [activeId, setActiveId] = useState<string>(initialId);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!items.some((i) => i.id === activeId) && items[0]) {
      setActiveId(items[0].id);
    }
  }, [items, activeId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    e.preventDefault();
    let nextIdx = idx;
    if (e.key === 'ArrowLeft') nextIdx = (idx - 1 + items.length) % items.length;
    if (e.key === 'ArrowRight') nextIdx = (idx + 1) % items.length;
    if (e.key === 'Home') nextIdx = 0;
    if (e.key === 'End') nextIdx = items.length - 1;
    const nextItem = items[nextIdx];
    if (nextItem) {
      setActiveId(nextItem.id);
      tabRefs.current[nextItem.id]?.focus();
    }
  };

  const activeItem = items.find((i) => i.id === activeId) ?? items[0];

  return (
    <div className={`tabs ${theme}${className ? ` ${className}` : ''}`}>
      <div className="tabs-list" role="tablist" aria-label={ariaLabel}>
        {items.map((item, idx) => {
          const isActive = item.id === activeId;
          return (
            <button
              key={item.id}
              ref={(el) => {
                tabRefs.current[item.id] = el;
              }}
              type="button"
              role="tab"
              id={`tab-${item.id}`}
              aria-selected={isActive}
              aria-controls={`tabpanel-${item.id}`}
              tabIndex={isActive ? 0 : -1}
              className={`tabs-tab${isActive ? ' tabs-tab--active' : ''}`}
              onClick={() => setActiveId(item.id)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
            >
              <span className="tabs-tab-label">{item.label}</span>
              {item.badge && <span className="tabs-tab-badge">{item.badge}</span>}
            </button>
          );
        })}
      </div>
      {activeItem && (
        <div
          className="tabs-panel"
          role="tabpanel"
          id={`tabpanel-${activeItem.id}`}
          aria-labelledby={`tab-${activeItem.id}`}
        >
          {activeItem.content}
        </div>
      )}
    </div>
  );
};

export default Tabs;
